export const CLOUDFLARE_TRACE_URL = "https://cloudflare.com/cdn-cgi/trace";
export const MAX_CLOUDFLARE_BLOCK_BODY_LENGTH = 4000;

export type CloudflareTrace = Record<string, string>;
export type CloudflareBlock = {
  status: number;
  statusText?: string;
  headers: Record<string, string>;
};

export type CloudflareTraceMetadata = {
  status?: number;
  error?: string;
  trace?: CloudflareTrace;
};
export type CloudflareBlockBodyMetadata = {
  body: string;
  truncated: boolean;
  originalLength?: number;
};

export const parseCloudflareTrace = (traceText: string): CloudflareTrace =>
  traceText.split("\n").reduce<CloudflareTrace>((trace, line) => {
    const separatorIndex = line.indexOf("=");
    if (separatorIndex <= 0) {
      return trace;
    }

    trace[line.slice(0, separatorIndex)] = line.slice(separatorIndex + 1);
    return trace;
  }, {});

export const getCloudflareBlock = (response: Response): CloudflareBlock => {
  const block: CloudflareBlock = {
    status: response.status,
    headers: {},
  };

  if (response.statusText) {
    block.statusText = response.statusText;
  }

  response.headers?.forEach((value, key) => {
    block.headers[key] = value;
  });

  return block;
};

const normalizeWhitespace = (value: string): string =>
  value
    .replace(/\\[rnt]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const extractCloudflareBlockBodyText = (body: string): string => {
  if (typeof DOMParser !== "undefined") {
    const document = new DOMParser().parseFromString(body, "text/html");
    document.querySelectorAll("script, style, noscript, svg").forEach(element => element.remove());

    return normalizeWhitespace([document.title, document.body?.textContent].filter(Boolean).join(" "));
  }

  return normalizeWhitespace(body);
};

export const getCloudflareBlockBody = async (response: Response): Promise<CloudflareBlockBodyMetadata | undefined> => {
  if (!response.headers.get("content-type")?.startsWith("text/html")) {
    return undefined;
  }

  return response.text().then(
    body => {
      const diagnosticBody = extractCloudflareBlockBodyText(body) || body;

      return diagnosticBody.length > MAX_CLOUDFLARE_BLOCK_BODY_LENGTH
        ? {
            body: diagnosticBody.slice(0, MAX_CLOUDFLARE_BLOCK_BODY_LENGTH),
            truncated: true,
            originalLength: diagnosticBody.length,
          }
        : {
            body: diagnosticBody,
            truncated: false,
          };
    },
    () => undefined,
  );
};

export const getSupportReferenceId = (cloudflareBlock: CloudflareBlock): string | undefined => {
  const referenceId = cloudflareBlock.headers["cf-ray"] ?? cloudflareBlock.headers["cf-request-id"];
  return referenceId || undefined;
};

export const fetchCloudflareTrace = async (signal?: AbortSignal): Promise<CloudflareTraceMetadata> => {
  try {
    const response = await fetch(CLOUDFLARE_TRACE_URL, {
      cache: "no-store",
      method: "GET",
      signal,
    });

    return {
      status: response.status,
      trace: parseCloudflareTrace(await response.text()),
    };
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      return {};
    }

    return {
      error: error instanceof Error ? error.message : String(error),
    };
  }
};
